import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DollarOutlined,
  HistoryOutlined,
  KeyOutlined,
  MobileOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  SendOutlined,
  ShoppingOutlined,
  TrophyOutlined,
  UserOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { SettingsActions } from '@portal/actions';
import {
  getCarrierProductList,
  getRechargeCarriers,
  rechargeTransaction,
} from '@portal/actions/device';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import FormattedBilletStatement from '@portal/components/FormattedBilletStatement/FormattedBilletStatement';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import ErrorsMessages from '@portal/enum/errors';
import {
  CarrierNameEnum,
  RechargeCategoryEnum,
  RechargeTypeEnum,
  TabStepRechargeIndexEnum,
} from '@portal/enum/recharge';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { CreateTopupRequestDto } from '@portal/models/module';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import {
  formatCurrency,
  maskCpf,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';
import * as MessageService from '@portal/services/message';
import { pinValidationResolver } from '@portal/utils/pinValidationResolver';
import { getOperatorIcon } from '@portal/utils/rechargeOperators';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import './RechargeSelection.less';

const COMPLETE_CELLPHONE_DIGITS = 11;
const COMPLETE_CELLPHONE_LENGTH_WITH_MASK = 16;
const COMPLETE_CPF_DIGITS = 11;
const COMPLETE_CPF_LENGTH_WITH_MASK = 14;

const EMPTY_CARRIER: models.Carrier = { name: '', id: '' };

const getInitialValue = (): models.CarrierProduct => ({
  name: translate('SCREENS.RECHARGE.VALUE.PLACEHOLDER'),
  amount: 0,
});

const CATEGORY_ICON_MAP: Record<number, React.ReactElement> = {
  [RechargeCategoryEnum.CELLPHONE]: <MobileOutlined />,
  [RechargeCategoryEnum.GAMES]: <TrophyOutlined />,
  [RechargeCategoryEnum.TV]: <PlayCircleOutlined />,
  [RechargeCategoryEnum.TRANSPORT]: <CarOutlined />,
  [RechargeCategoryEnum.DIGITAL_CONTENT]: <ShoppingOutlined />,
};

const RechargeSelection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    device: { carriers, carrierProductList },
    theme: { currentTheme },
    user: { me },
    account: { balance },
    settings: { settings },
  } = useReduxState();

  const [tabKey, setTabKey] = useState(TabStepRechargeIndexEnum.MODE);
  const [cellphone, setCellphone] = useState('');
  const [value, setValue] = useState<models.CarrierProduct>(getInitialValue());
  const [carrier, setCarrier] = useState<models.Carrier>(EMPTY_CARRIER);
  const [hasErrorOnCarriers, setHasErrorOnCarriers] = useState(false);
  const [hasInvalidAreaCode, setHasInvalidAreaCode] = useState(false);
  const [hasErrorOnCarrierProductList, setHasErrorOnCarrierProductList] =
    useState(false);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [lastFetchedCarrierId, setLastFetchedCarrierId] = useState<string>('');
  const [modalPin, setModalPin] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [receiptData, setReceiptData] = useState<models.RechargeReceipt | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tvSubscriberDocument, setTvSubscriberDocument] = useState('');
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [pinCopied, setPinCopied] = useState(false);

  useEffect(() => {
    dispatch(SettingsActions.getSettings());
  }, [dispatch]);

  const isCellphoneComplete = useMemo(
    () => unmaskField(cellphone).length === COMPLETE_CELLPHONE_DIGITS,
    [cellphone]
  );

  const getDDD = useCallback(() => {
    return unmaskField(cellphone).slice(0, 2);
  }, [cellphone]);

  const handleCarriersCallback = useCallback((err: Error | null) => {
    if (err?.message === ErrorsMessages.INVALID_CODE_AREA) {
      setIsLoadingCarriers(false);
      setHasInvalidAreaCode(true);
      setHasErrorOnCarriers(false);
      return;
    }

    setIsLoadingCarriers(false);
    setHasInvalidAreaCode(false);
    setHasErrorOnCarriers(!!err);
  }, []);

  const handleProductsCallback = useCallback((err: Error | null) => {
    setIsLoadingProducts(false);
    setHasErrorOnCarrierProductList(!!err);
  }, []);

  const getBaseCarrierName = (name: string): string => {
    const KNOWN_CARRIERS = Object.values(CarrierNameEnum);

    const normalized = name.toLowerCase().trim();
    const match = KNOWN_CARRIERS.find((carrier) =>
      normalized.startsWith(carrier)
    );
    return match || normalized;
  };

  const CATEGORY_OPTIONS = useMemo(() => {
    const allOptions = [
      {
        value: RechargeCategoryEnum.CELLPHONE,
        name: translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.CELLPHONE'),
      },
      {
        value: RechargeCategoryEnum.GAMES,
        name: translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.GAMES'),
      },
      {
        value: RechargeCategoryEnum.TV,
        name: translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.TV'),
      },
      {
        value: RechargeCategoryEnum.TRANSPORT,
        name: translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.TRANSPORT'),
      },
      {
        value: RechargeCategoryEnum.DIGITAL_CONTENT,
        name: translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.DIGITAL_CONTENT'),
      },
    ];

    if (!carriers || carriers.length === 0) return allOptions;

    const availableCategories = new Set(carriers.map((c) => c.category));
    return allOptions.filter((opt) => availableCategories.has(opt.value));
  }, [carriers]);

  const uniqueCarriers = useMemo(() => {
    if (!carriers) return [];

    const filtered = selectedCategory
      ? carriers.filter((c) => c.category === selectedCategory)
      : carriers;

    return filtered.filter(
      (carrier, index, self) =>
        index ===
        self.findIndex(
          (c) => getBaseCarrierName(c.name) === getBaseCarrierName(carrier.name)
        )
    );
  }, [carriers, selectedCategory]);

  const ddd = useMemo(() => {
    if (!isCellphoneComplete) return null;
    return unmaskField(cellphone).slice(0, 2);
  }, [cellphone, isCellphoneComplete]);

  useEffect(() => {
    return () => {
      setReceiptData(null);
      setCellphone('');
      setCarrier(EMPTY_CARRIER);
      setValue(getInitialValue());
    };
  }, []);

  useEffect(() => {
    setCarrier(EMPTY_CARRIER);
    setValue(getInitialValue());

    if (!ddd) {
      setHasErrorOnCarriers(false);
      setHasInvalidAreaCode(false);
      setHasErrorOnCarrierProductList(false);
      return;
    }

    setIsLoadingCarriers(true);
    dispatch(getRechargeCarriers(ddd, handleCarriersCallback));
  }, [ddd]);

  const handleCategoryChange = useCallback((categoryValue: number) => {
    setSelectedCategory(categoryValue);
    setCarrier(EMPTY_CARRIER);
    setValue(getInitialValue());
    setHasErrorOnCarrierProductList(false);
    if (categoryValue !== RechargeCategoryEnum.CELLPHONE) {
      setCellphone('');
    }
    if (categoryValue !== RechargeCategoryEnum.TV) {
      setTvSubscriberDocument('');
    }
  }, []);

  const handleChooseCarrier = useCallback((selectedCarrier: models.Carrier) => {
    setCarrier({
      id: selectedCarrier.providerId ?? '',
      name: selectedCarrier.name,
      tipoRecargaNameProvider: selectedCarrier.tipoRecargaNameProvider,
    });
    setHasErrorOnCarriers(false);
    setHasInvalidAreaCode(false);
    setHasErrorOnCarrierProductList(false);
    setValue(getInitialValue());
    setLastFetchedCarrierId('');
  }, []);

  const handleAdvanceToValue = () => {
    const isCellphoneCategory =
      selectedCategory === RechargeCategoryEnum.CELLPHONE;

    if ((isCellphoneCategory && !isCellphoneComplete) || !carrier.id) {
      return;
    }

    const dddToUse = isCellphoneCategory
      ? getDDD()
      : settings?.celcoinDefaultPhone?.areaCode;

    setTabKey(TabStepRechargeIndexEnum.VALUE);

    if (carrier.id === lastFetchedCarrierId && carrierProductList?.length) {
      return;
    }

    setIsLoadingProducts(true);
    setLastFetchedCarrierId(carrier.id);

    dispatch(
      getCarrierProductList(
        dddToUse ?? '',
        carrier.id ?? '',
        handleProductsCallback
      )
    );
  };

  const handleAdvanceToConfirmation = () => {
    if (!value.amount || value.amount === 0) {
      return;
    }

    const isValidProduct = carrierProductList?.some(
      (product) => product.amount === value.amount
    );

    if (!isValidProduct) {
      MessageService.error('PAGES.RECHARGE.CONFIRMATION.INVALID_PRODUCT_VALUE');
      return;
    }

    if (Number(balance?.available ?? 0) < value.amount) {
      MessageService.error('INSUFFICIENT_BALANCE');
      return;
    }

    setTabKey(TabStepRechargeIndexEnum.CONFIRMATION);
  };

  const handleRecharge = async (pin: string) => {
    setModalPin(false);

    if (!pin || isRechargeButtonDisabled || isSubmitting) return;

    setIsSubmitting(true);

    const isCellphone = selectedCategory === RechargeCategoryEnum.CELLPHONE;
    const isTv = selectedCategory === RechargeCategoryEnum.TV;

    const rawPhone = unmaskField(cellphone);
    const providerId = Number(carrier?.id);

    const stateCode = isCellphone
      ? Number(rawPhone.slice(0, 2))
      : Number(settings?.celcoinDefaultPhone?.areaCode);

    const phoneNumber = isCellphone
      ? Number(rawPhone.slice(2))
      : Number(settings?.celcoinDefaultPhone?.phone);

    const isValidProduct = carrierProductList?.some(
      (product) => product.amount === value.amount
    );

    const isInvalidCellphone =
      isCellphone && (Number.isNaN(stateCode) || Number.isNaN(phoneNumber));

    if (isInvalidCellphone || !providerId || !isValidProduct) {
      MessageService.error('PAGES.RECHARGE.CONFIRMATION.INVALID_PRODUCT_VALUE');
      setIsSubmitting(false);
      return;
    }

    const encryptedPIN = await encrypt(pin);

    const tvSignerCode = unmaskField(tvSubscriberDocument);

    const getSignerCode = () => {
      if (isCellphone) return rawPhone;
      if (isTv) return tvSignerCode;
      return me?.account?.taxIdentifier ?? '';
    };

    const body: CreateTopupRequestDto = {
      value: value.amount,
      cpfCnpj: me?.account?.taxIdentifier ?? '',
      signerCode: getSignerCode(),
      providerId,
      phone: {
        stateCode,
        countryCode: Number(RechargeTypeEnum.COUNTRY_CODE),
        number: phoneNumber,
      },
      type: carrier.tipoRecargaNameProvider?.toString() ?? '',
      category: selectedCategory as RechargeCategoryEnum,
    };

    dispatch(
      rechargeTransaction(body, encryptedPIN, (err, data) => {
        setIsSubmitting(false);
        setPinCopied(false);

        if (err) {
          const { message, isPinError } = pinValidationResolver({
            err,
            fallbackKey: 'PAGES.RECHARGE.CONFIRMATION.ERROR',
          });

          if (isPinError) {
            MessageService.error(message);
            setModalPin(true);
            return;
          }

          setTransactionError(translate(message));
          setTabKey(TabStepRechargeIndexEnum.RECEIPT);
          return;
        }

        setTransactionError(null);

        if (data) {
          setReceiptData(data);
        }

        setTabKey(TabStepRechargeIndexEnum.RECEIPT);
      })
    );
  };

  const isRechargeButtonDisabled = useMemo(() => {
    const isCellphone = selectedCategory === RechargeCategoryEnum.CELLPHONE;
    const isTv = selectedCategory === RechargeCategoryEnum.TV;
    const tvDocComplete =
      unmaskField(tvSubscriberDocument).length === COMPLETE_CPF_DIGITS;
    return (
      !carrier.id ||
      !value.amount ||
      value.amount === 0 ||
      (isCellphone && !isCellphoneComplete) ||
      (isTv && !tvDocComplete) ||
      isLoadingCarriers ||
      isLoadingProducts ||
      hasErrorOnCarriers ||
      hasErrorOnCarrierProductList ||
      isSubmitting
    );
  }, [
    selectedCategory,
    isCellphoneComplete,
    tvSubscriberDocument,
    carrier.id,
    value.amount,
    isLoadingCarriers,
    isLoadingProducts,
    hasErrorOnCarriers,
    hasErrorOnCarrierProductList,
    isSubmitting,
  ]);

  const renderLoadingState = (titleKey: string, className: string) => (
    <div className={className}>
      <ThemedText variant="body">{translate(titleKey)}</ThemedText>
      <ThemedText variant="body">{translate('APPLICATION.LOADING')}</ThemedText>
    </div>
  );

  const renderErrorState = (
    titleKey: string,
    errorKey: string,
    className: string
  ) => (
    <div className={className}>
      <ThemedText variant="body">{translate(titleKey)}</ThemedText>
      <ThemedText variant="body">{translate(errorKey)}</ThemedText>
    </div>
  );

  const renderInputNumber = () => {
    if (selectedCategory !== RechargeCategoryEnum.CELLPHONE) return null;

    return (
      <div className="recharge-selection__input-number">
        <ThemedText variant="body">
          {translate('PAGES.RECHARGE.CELLPHONE.INPUT_NUMBER.TITLE')}
        </ThemedText>
        <AdvancedInput
          testId="cellphone-input-number"
          placeholder={translate(
            'PAGES.RECHARGE.CELLPHONE.INPUT_NUMBER.PLACEHOLDER'
          )}
          value={cellphone}
          onChange={(value) => {
            const onlyNumbers = unmaskField(value);
            if (onlyNumbers.length <= COMPLETE_CELLPHONE_DIGITS) {
              setCellphone(maskPhoneInput(onlyNumbers));
            }
          }}
          maxLength={COMPLETE_CELLPHONE_LENGTH_WITH_MASK}
        />
      </div>
    );
  };

  const renderTvSubscriber = () => {
    if (selectedCategory !== RechargeCategoryEnum.TV) return null;

    return (
      <div className="recharge-selection__tv-subscriber">
        <ThemedText variant="body">
          {translate('PAGES.RECHARGE.TV.SUBSCRIBER.TITLE')}
        </ThemedText>
        <AdvancedInput
          testId="tv-subscriber-document"
          placeholder={translate(
            'PAGES.RECHARGE.TV.SUBSCRIBER.DOCUMENT_PLACEHOLDER'
          )}
          value={tvSubscriberDocument}
          onChange={(val) => {
            const onlyNumbers = unmaskField(val);
            if (onlyNumbers.length <= COMPLETE_CPF_DIGITS) {
              setTvSubscriberDocument(maskCpf(onlyNumbers));
            }
          }}
          maxLength={COMPLETE_CPF_LENGTH_WITH_MASK}
        />
      </div>
    );
  };

  const renderCategorySelect = () => {
    if (isLoadingCarriers || hasInvalidAreaCode || hasErrorOnCarriers) {
      return null;
    }

    if (!carriers || carriers.length === 0) {
      return null;
    }

    return (
      <div className="recharge-selection__category">
        <ThemedText variant="body">
          {translate('PAGES.RECHARGE.CELLPHONE.CATEGORY.TITLE')}
        </ThemedText>
        <div className="recharge-selection__category__pills">
          {CATEGORY_OPTIONS.map((option) => {
            const isSelected = selectedCategory === option.value;
            return (
              <button
                key={option.value}
                className={`recharge-selection__category__pill${
                  isSelected
                    ? ' recharge-selection__category__pill--selected'
                    : ''
                }`}
                onClick={() => handleCategoryChange(option.value)}
              >
                {CATEGORY_ICON_MAP[option.value]}
                <ThemedText
                  variant="body"
                  className="recharge-selection__category__pill__name"
                >
                  {option.name}
                </ThemedText>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCarrier = () => {
    if (!selectedCategory) {
      return null;
    }

    const isCellphoneCategory =
      selectedCategory === RechargeCategoryEnum.CELLPHONE;

    if (isCellphoneCategory && !isCellphoneComplete) {
      return null;
    }

    const titleKey = 'PAGES.RECHARGE.CELLPHONE.CARRIER.TITLE';

    if (isLoadingCarriers) {
      return renderLoadingState(titleKey, 'recharge-selection__carrier');
    }

    if (hasInvalidAreaCode) {
      return renderErrorState(
        titleKey,
        'PAGES.RECHARGE.CELLPHONE.CARRIER.INVALID_AREA_CODE',
        'recharge-selection__carrier'
      );
    }

    if (hasErrorOnCarriers || !uniqueCarriers || uniqueCarriers.length === 0) {
      return renderErrorState(
        titleKey,
        'PAGES.RECHARGE.CELLPHONE.CARRIER.ERROR',
        'recharge-selection__carrier'
      );
    }

    return (
      <div className="recharge-selection__carrier">
        <ThemedText variant="body">
          {translate('PAGES.RECHARGE.CELLPHONE.CARRIER.TITLE')}
        </ThemedText>
        <div className="recharge-selection__operatorContainer">
          {uniqueCarriers.map((item) => {
            const operatorIcon = getOperatorIcon(item.name);
            const isSelected = carrier.id === item.providerId;

            const renderIcon = () => {
              if (!operatorIcon) {
                return (
                  <ThemedText variant="body">
                    {item.name.split(' ')[0]}
                  </ThemedText>
                );
              }
              if (operatorIcon.type === 'svg') {
                const SvgIcon = operatorIcon.component;
                return (
                  <SvgIcon className="recharge-selection__operator__icon" />
                );
              }
              return (
                <img
                  src={operatorIcon.src}
                  alt={item.name}
                  className="recharge-selection__operator__icon"
                />
              );
            };

            return (
              <div key={item.id}>
                <button
                  className="recharge-selection__operator"
                  style={{
                    border: isSelected
                      ? `1px solid ${currentTheme.primaryColor}`
                      : `1px solid ${currentTheme.borderColor}`,
                  }}
                  disabled={isLoadingProducts}
                  onClick={() => handleChooseCarrier(item)}
                >
                  {renderIcon()}
                </button>
                <ThemedText variant="body">{item.name}</ThemedText>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderValue = () => {
    if (!carrier.id) {
      return null;
    }

    const titleKey = 'PAGES.RECHARGE.CELLPHONE.VALUE.TITLE';

    if (isLoadingProducts) {
      return renderLoadingState(titleKey, 'recharge-selection__value');
    }

    if (hasErrorOnCarrierProductList) {
      return renderErrorState(
        titleKey,
        'PAGES.RECHARGE.CELLPHONE.VALUE.ERROR',
        'recharge-selection__value'
      );
    }

    if (!carrierProductList || carrierProductList.length === 0) {
      return renderErrorState(
        titleKey,
        'PAGES.RECHARGE.CELLPHONE.VALUE.EMPTY',
        'recharge-selection__value'
      );
    }

    return (
      <div className="recharge-selection__value">
        <ThemedText variant="body">{translate(titleKey)}</ThemedText>
        <div className="recharge-selection__values">
          {carrierProductList.map((item) => {
            const isSelected = item.amount === value.amount;
            const formatted = formatCurrency(item.amount);
            const symbol = 'R$';
            const amount = formatted.replace(/^R\$\s*/u, '');

            return (
              <button
                key={item.name}
                className={`recharge-selection__values__item ${
                  isSelected
                    ? ' recharge-selection__values__item--selected'
                    : ''
                }`}
                onClick={() =>
                  setValue({ name: item.name, amount: item.amount })
                }
              >
                {isSelected ? (
                  <span className="recharge-selection__values__item__check">
                    <CheckCircleOutlined />
                  </span>
                ) : null}
                <div className="recharge-selection__values__item__amount">
                  <ThemedText className="recharge-selection__values__item__amount__symbol">
                    {symbol}
                  </ThemedText>
                  <ThemedText className="recharge-selection__values__item__amount__number">
                    {amount}
                  </ThemedText>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleGoBack = (targetStep: TabStepRechargeIndexEnum) => {
    if (Number(targetStep) >= Number(tabKey)) return;
    setHoveredIndex(null);
    setTabKey(targetStep);
  };

  const renderNavigationButtons = (
    previousStep: TabStepRechargeIndexEnum,
    onNext: () => void,
    nextDisabled: boolean,
    nextButtonText = translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')
  ) => (
    <div className="recharge-selection__buttons">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="outlined"
        onClick={() => handleGoBack(previousStep)}
      />
      <AdvancedButton
        text={nextButtonText}
        onClick={onNext}
        disabled={nextDisabled}
      />
    </div>
  );

  const isCellphoneCategory =
    selectedCategory === RechargeCategoryEnum.CELLPHONE;
  const isTvCategoryStep = selectedCategory === RechargeCategoryEnum.TV;
  const isTvDocumentComplete =
    unmaskField(tvSubscriberDocument).length === COMPLETE_CPF_DIGITS;
  const isDataStepDisabled =
    !selectedCategory ||
    !carrier.id ||
    isLoadingCarriers ||
    isLoadingProducts ||
    (isCellphoneCategory && !isCellphoneComplete) ||
    (isTvCategoryStep && !isTvDocumentComplete);

  const DataStepButtons = (
    <div className="recharge-selection__buttons">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="outlined"
        onClick={() => handleGoBack(TabStepRechargeIndexEnum.MODE)}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        onClick={handleAdvanceToValue}
        disabled={isDataStepDisabled}
      />
    </div>
  );

  const ValueStepButtons = renderNavigationButtons(
    TabStepRechargeIndexEnum.DATA,
    handleAdvanceToConfirmation,
    !value.amount || value.amount === 0
  );

  const ConfirmationStepButtons = renderNavigationButtons(
    TabStepRechargeIndexEnum.VALUE,
    () => setModalPin(true),
    isRechargeButtonDisabled,
    translate('COMPONENTS.ADVANCED_BUTTON.CONFIRM')
  );

  const renderConfirmationDetail = () => {
    const isCellphoneCat = selectedCategory === RechargeCategoryEnum.CELLPHONE;
    const isTvCat = selectedCategory === RechargeCategoryEnum.TV;
    const isPinDelivery =
      selectedCategory === RechargeCategoryEnum.GAMES ||
      selectedCategory === RechargeCategoryEnum.DIGITAL_CONTENT;

    const categoryLabel: Record<number, string> = {
      [RechargeCategoryEnum.CELLPHONE]: translate(
        'PAGES.RECHARGE.CELLPHONE.CATEGORY.CELLPHONE'
      ),
      [RechargeCategoryEnum.GAMES]: translate(
        'PAGES.RECHARGE.CELLPHONE.CATEGORY.GAMES'
      ),
      [RechargeCategoryEnum.TV]: translate(
        'PAGES.RECHARGE.CELLPHONE.CATEGORY.TV'
      ),
      [RechargeCategoryEnum.TRANSPORT]: translate(
        'PAGES.RECHARGE.CELLPHONE.CATEGORY.TRANSPORT'
      ),
      [RechargeCategoryEnum.DIGITAL_CONTENT]: translate(
        'PAGES.RECHARGE.CELLPHONE.CATEGORY.DIGITAL_CONTENT'
      ),
    };

    const recipientPrimary = isCellphoneCat
      ? cellphone
      : isTvCat
      ? tvSubscriberDocument
      : carrier.name;

    const recipientSecondary = isCellphoneCat
      ? carrier.name
      : isTvCat
      ? undefined
      : undefined;

    const operatorEntry = getOperatorIcon(getBaseCarrierName(carrier.name));
    const operatorIconElement = operatorEntry ? (
      operatorEntry.type === 'svg' ? (
        <operatorEntry.component style={{ width: '100%', height: '100%' }} />
      ) : (
        <img
          src={operatorEntry.src}
          alt={carrier.name}
          className="recharge-selection__image"
        />
      )
    ) : null;

    const recipientIcon =
      operatorIconElement ??
      (isCellphoneCat ? (
        <MobileOutlined />
      ) : isTvCat ? (
        <PlayCircleOutlined />
      ) : (
        <WifiOutlined />
      ));

    const renderReviewCard = (
      icon: React.ReactElement,
      label: string,
      primary: string,
      secondary?: string,
      highlight?: boolean,
      fullWidth?: boolean,
      operatorImage?: React.ReactElement
    ) => (
      <div
        key={label}
        className={[
          'recharge-selection__review-card',
          highlight ? 'recharge-selection__review-card--highlight' : '',
          fullWidth ? 'recharge-selection__review-card--full' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="recharge-selection__review-card__header">
          {operatorImage ? (
            <span className="recharge-selection__review-card__operator-image">
              {operatorImage}
            </span>
          ) : (
            <span className="recharge-selection__review-card__icon">
              {icon}
            </span>
          )}
          <ThemedText
            variant="body"
            className="recharge-selection__review-card__label"
          >
            {label}
          </ThemedText>
        </div>
        <ThemedText className="recharge-selection__review-card__primary">
          {primary}
        </ThemedText>
        {secondary ? (
          <ThemedText
            variant="body"
            className="recharge-selection__review-card__secondary"
          >
            {secondary}
          </ThemedText>
        ) : null}
      </div>
    );

    return (
      <div className="recharge-selection__review">
        <div className="recharge-selection__review-grid">
          {renderReviewCard(
            <UserOutlined />,
            translate('PAGES.RECHARGE.CONFIRMATION.WHO_PAYS'),
            me?.name ?? '',
            maskCpf(me?.account?.taxIdentifier ?? '')
          )}
          {!!(isCellphoneCat || isTvCat) &&
            renderReviewCard(
              recipientIcon,
              translate('PAGES.RECHARGE.CONFIRMATION.WHO_RECEIVES'),
              recipientPrimary,
              recipientSecondary
            )}
          {renderReviewCard(
            CATEGORY_ICON_MAP[selectedCategory ?? 0] ?? <ShoppingOutlined />,
            translate('PAGES.RECHARGE.CONFIRMATION.PRODUCT'),
            value.name,
            categoryLabel[selectedCategory ?? 0],
            undefined,
            undefined,
            operatorIconElement ?? undefined
          )}
          {renderReviewCard(
            <DollarOutlined />,
            translate('PAGES.RECHARGE.CONFIRMATION.VALUE'),
            formatCurrency(Number(value.amount)),
            undefined,
            true,
            true
          )}
          {renderReviewCard(
            isPinDelivery ? <KeyOutlined /> : <SendOutlined />,
            translate('PAGES.RECHARGE.CONFIRMATION.DELIVERY_METHOD'),
            isPinDelivery
              ? translate('PAGES.RECHARGE.CONFIRMATION.DELIVERY_PIN')
              : translate('PAGES.RECHARGE.CONFIRMATION.DELIVERY_DIRECT')
          )}
        </div>
      </div>
    );
  };

  const formatReceiptText = (text: string): string => {
    return text
      .replace(/<VIA1>/g, '')
      .replace(/<\/VIA1>/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
  };

  const handleNewRecharge = () => {
    setReceiptData(null);
    setTransactionError(null);
    setPinCopied(false);
    setCarrier(EMPTY_CARRIER);
    setCellphone('');
    setValue(getInitialValue());
    setSelectedCategory(null);
    setTvSubscriberDocument('');
    setTabKey(TabStepRechargeIndexEnum.MODE);
    setHoveredIndex(null);
    setIsSubmitting(false);
  };

  const isPinCategory =
    selectedCategory === RechargeCategoryEnum.GAMES ||
    selectedCategory === RechargeCategoryEnum.DIGITAL_CONTENT;

  const extractPinFromReceipt = (text: string): string | null => {
    const match = text.match(/(?:PIN|C[oó]digo|Code)[:\s]+([A-Z0-9-]{4,})/i);
    return match ? match[1].trim() : null;
  };

  const handleCopyPin = (pin: string) => {
    navigator.clipboard.writeText(pin).then(() => {
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 3000);
    });
  };

  const renderReceipt = () => {
    if (transactionError) {
      return (
        <div className="recharge-selection__result recharge-selection__result--error">
          <div className="recharge-selection__result__icon recharge-selection__result__icon--error">
            <CloseCircleOutlined />
          </div>
          <ThemedText className="recharge-selection__result__title">
            {translate('PAGES.RECHARGE.RECEIPT.ERROR_TITLE')}
          </ThemedText>
          <ThemedText
            variant="body"
            className="recharge-selection__result__subtitle"
          >
            {transactionError}
          </ThemedText>
          <div className="recharge-selection__result__actions">
            <AdvancedButton
              text={translate('PAGES.RECHARGE.RECEIPT.TRY_AGAIN')}
              onClick={() => {
                setIsSubmitting(false);
                setTransactionError(null);
                setTabKey(TabStepRechargeIndexEnum.CONFIRMATION);
              }}
            />
            <AdvancedButton
              text={translate('PAGES.RECHARGE.RECEIPT.NEW_RECHARGE')}
              variant="outlined"
              onClick={handleNewRecharge}
            />
          </div>
        </div>
      );
    }

    const formattedText = receiptData?.receiptformatted
      ? formatReceiptText(receiptData.receiptformatted)
      : null;
    const pinCode =
      isPinCategory && formattedText
        ? extractPinFromReceipt(formattedText)
        : null;

    return (
      <div className="recharge-selection__result recharge-selection__result--success">
        <div className="recharge-selection__result__icon recharge-selection__result__icon--success">
          <CheckCircleOutlined />
        </div>
        <ThemedText className="recharge-selection__result__title">
          {translate('PAGES.RECHARGE.RECEIPT.SUCCESS_TITLE')}
        </ThemedText>
        <ThemedText
          variant="body"
          className="recharge-selection__result__subtitle"
        >
          {translate('PAGES.RECHARGE.RECEIPT.SUCCESS_SUBTITLE')}
        </ThemedText>
        {pinCode ? (
          <div className="recharge-selection__result__pin">
            <ThemedText
              variant="body"
              className="recharge-selection__result__pin__label"
            >
              {translate('PAGES.RECHARGE.RECEIPT.PIN_TITLE')}
            </ThemedText>
            <div className="recharge-selection__result__pin__box">
              <ThemedText className="recharge-selection__result__pin__code">
                {pinCode}
              </ThemedText>
              <button
                className="recharge-selection__result__pin__copy transparent-button"
                onClick={() => handleCopyPin(pinCode)}
              >
                <CopyOutlined />
                <ThemedText variant="body">
                  {translate(
                    pinCopied
                      ? 'PAGES.RECHARGE.RECEIPT.PIN_COPIED'
                      : 'PAGES.RECHARGE.RECEIPT.PIN_COPY'
                  )}
                </ThemedText>
              </button>
            </div>
            <ThemedText
              variant="body"
              className="recharge-selection__result__pin__instructions"
            >
              {translate('PAGES.RECHARGE.RECEIPT.PIN_INSTRUCTIONS')}
            </ThemedText>
          </div>
        ) : null}
        {formattedText ? (
          <div className="recharge-selection__result__voucher">
            <ThemedText
              variant="body"
              className="recharge-selection__result__voucher__label"
            >
              {translate('PAGES.RECHARGE.RECEIPT.VOUCHER_LABEL')}
            </ThemedText>
            <FormattedBilletStatement billet={formattedText} />
          </div>
        ) : null}
        <div className="recharge-selection__result__actions">
          <AdvancedButton
            text={translate('PAGES.RECHARGE.RECEIPT.NEW_RECHARGE')}
            onClick={handleNewRecharge}
          />
        </div>
      </div>
    );
  };

  const renderModeSelector = () => {
    const actions = [
      {
        icon: <PlusCircleOutlined />,
        title: translate('PAGES.RECHARGE.MODE.NEW.TITLE'),
        text: translate('PAGES.RECHARGE.MODE.NEW.DESCRIPTION'),
        type: 'ted',
        onClick: () => {
          setCellphone('');
          setCarrier(EMPTY_CARRIER);
          setValue(getInitialValue());
          setSelectedCategory(null);
          setHasErrorOnCarriers(false);
          setHasInvalidAreaCode(false);
          setHasErrorOnCarrierProductList(false);
          setIsLoadingCarriers(true);
          dispatch(
            getRechargeCarriers(
              settings?.celcoinDefaultPhone?.areaCode ?? '',
              handleCarriersCallback
            )
          );
          setTabKey(TabStepRechargeIndexEnum.DATA);
        },
        hide: false,
      },
      {
        icon: <HistoryOutlined />,
        title: translate('PAGES.RECHARGE.MODE.HISTORY.TITLE'),
        text: translate('PAGES.RECHARGE.MODE.HISTORY.DESCRIPTION'),
        type: 'history',
        onClick: () =>
          navigate(getRouteStackPath('RECHARGE', 'RECHARGE_HISTORY_PAGE')),
        hide: false,
      },
    ];

    return (
      <div className="recharge-selection__grid">
        {actions
          ?.filter((action) => !action.hide)
          .map((action) => (
            <button
              key={action.title}
              className="recharge-selection__grid__button transparent-button"
              onClick={action.onClick}
              onMouseEnter={() => setHoveredIndex(action.type)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                border: `2px solid ${currentTheme.borderColor}`,
                background:
                  hoveredIndex === action.type
                    ? currentTheme.background
                    : 'transparent',
              }}
            >
              <div
                className="recharge-selection__grid__button__icon"
                style={{ background: currentTheme.background }}
              >
                {action.icon}
              </div>
              <div>
                <ThemedText className="recharge-selection__grid__button__title">
                  {action.title}
                </ThemedText>
                <ThemedText
                  variant="body"
                  className="recharge-selection__grid__button__description"
                >
                  {action.text}
                </ThemedText>
              </div>
            </button>
          ))}
      </div>
    );
  };

  const stepOptions = [
    {
      value: TabStepRechargeIndexEnum.MODE,
      label: translate('PAGES.RECHARGE.TABS.MODE'),
      children: renderModeSelector(),
    },
    {
      value: TabStepRechargeIndexEnum.DATA,
      label: translate('PAGES.RECHARGE.TABS.DATA'),
      children: (
        <>
          {renderCategorySelect()}
          {renderTvSubscriber()}
          {renderInputNumber()}
          {renderCarrier()}
        </>
      ),
    },
    {
      value: TabStepRechargeIndexEnum.VALUE,
      label: translate('PAGES.RECHARGE.TABS.VALUE'),
      children: renderValue(),
    },
    {
      value: TabStepRechargeIndexEnum.CONFIRMATION,
      label: translate('PAGES.RECHARGE.TABS.CONFIRMATION'),
      children: renderConfirmationDetail(),
    },
    {
      value: TabStepRechargeIndexEnum.RECEIPT,
      label: translate('PAGES.RECHARGE.TABS.RECEIPT'),
      children: renderReceipt(),
    },
  ];

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.RECHARGE.SELECTION.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            title: translate('PAGES.RECHARGE.SELECTION.TITLE'),
            active: false,
          },
        ]}
      />
      <div className="recharge-selection">
        <AdvancedPanelContentHeader
          testId="recharge-selection-header"
          title={translate('PAGES.RECHARGE.SELECTION.TITLE')}
          description={translate('PAGES.RECHARGE.SELECTION.DESCRIPTION')}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
        />
        <AdvancedSteps
          testId="advanced-steps-recharge"
          steps={stepOptions}
          currentStep={tabKey}
          onStepChange={(step) =>
            handleGoBack(step as TabStepRechargeIndexEnum)
          }
        />
        {tabKey === TabStepRechargeIndexEnum.DATA && DataStepButtons}
        {tabKey === TabStepRechargeIndexEnum.VALUE && ValueStepButtons}
        {tabKey === TabStepRechargeIndexEnum.CONFIRMATION &&
          ConfirmationStepButtons}
      </div>
      <ConfirmationModal
        visible={modalPin}
        setVisible={setModalPin}
        handleConfirmation={handleRecharge}
      />
    </>
  );
};

export default RechargeSelection;
